import React from 'react';
import { NormalizedCacheObject } from 'apollo-cache-inmemory';
import ApolloClient from 'apollo-client';
import { MockApolloClient } from 'mock-apollo-client';
import { MenuItem, OutlinedInput, Select, Theme, makeStyles } from '@material-ui/core';

import { useGetCptCodesQuery } from './documents/getCptCodes.generated';
import { CptCodeNode, ServiceType } from '../../generated/webapp_gql';
import { colors } from '../../themes/colors';
import { textInputStyle } from '../../themes/input';
import { BillingCodesInfoBubble } from '../Clients/BillingCodesInfoBubble';

interface Props {
  companyUuid: string;
  serviceType: ServiceType;
  onSelectCptCode: (v: string) => void;
  selectedCptCode?: string;
  memberClient: ApolloClient<NormalizedCacheObject> | MockApolloClient;
}

export enum ChooseCPTTestID {
  Input = 'ChooseCPTTestID.Input',
  Label = 'ChooseCPTTestID.Label',
}

const MOST_COMMON = 'MOST_COMMON';

const useStyles = makeStyles((theme: Theme) => ({
  ...textInputStyle(theme),
  selectRoot: {
    flex: '1 1 auto',
    display: 'flex',
    overflow: 'hidden',
  },
  muiRoot: {
    display: 'block',
    backgroundColor: 'transparent !important',
  },
  selectContainer: {
    color: colors.gray18,
    fontSize: '14px',
    maxHeight: 40,
    flex: '1 1 auto',
    minWidth: 0,
    fontFamily: 'Avenir-Medium',
    borderRadius: 6,
    '& input': {
      cursor: 'pointer',
    },
    '& fieldset': {
      maxHeight: 40,
      borderColor: '#E9EEF4',
      borderWidth: 1,
    },
    '&:hover fieldset': {
      borderColor: `${colors.blue6} !important`,
    },
    '& .Mui-focused fieldset': {
      maxHeight: 40,
      borderColor: `${colors.blue6} !important`,
    },
    '& div.Mui-error fieldset': {
      borderColor: '#e53e3e !important',
    },
    '& .MuiButtonBase-root.MuiIconButton-root': {
      outline: 'none',
      position: 'absolute',
      right: 0,
      marginTop: 2,
      color: colors.gray16,
    },
  },
  menuItemStyle: {
    fontSize: 12,
    fontWeight: 500,
    color: colors.black2,
  },
}));

const SELECT_PLACEHOLDER_TEXT_VALUE = 'select_cpt_code';
const GROUP_NAME_CLASS =
  'text-xs uppercase text-gray-850 font-extrabold leading-8 pl-4 cursor-default';

export const ChooseCPTCode: React.FC<Props> = ({
  companyUuid,
  serviceType,
  memberClient,
  onSelectCptCode,
  selectedCptCode,
}) => {
  const { data } = useGetCptCodesQuery({
    client: memberClient,
    variables: {
      companyUuid,
      serviceType,
    },
  });

  const { menuItemStyle, muiRoot, selectContainer, selectRoot } = useStyles();
  const commonCodes: CptCodeNode[] = [];
  const additionalCodes: CptCodeNode[] = [];

  (data?.getCptCodes || []).forEach((codeObj: CptCodeNode | null) => {
    if (codeObj) {
      if (codeObj.groupName === MOST_COMMON) {
        commonCodes.push(codeObj);
      } else {
        additionalCodes.push(codeObj);
      }
    }
  });

  return (
    <>
      <div className="flex items-center">
        <label
          className="text-sm font-black text-gray-800 leading-loose"
          data-testid={ChooseCPTTestID.Label}
        >
          <span className="text-red-500">*</span> CPT Code
        </label>
        <BillingCodesInfoBubble />
      </div>
      <div className={selectRoot}>
        <Select
          id="select-cpt-code"
          value={selectedCptCode || SELECT_PLACEHOLDER_TEXT_VALUE}
          className={selectContainer}
          input={<OutlinedInput data-testid={ChooseCPTTestID.Input} />}
          classes={{ root: muiRoot }}
          onChange={({ target: { value } }) => onSelectCptCode(value as string)}
        >
          <div className={GROUP_NAME_CLASS} tabIndex={0}>
            Most common codes
          </div>
          {commonCodes.map(
            ({ code, description }) =>
              code && (
                <MenuItem key={code} value={code} className={menuItemStyle}>
                  {code} &bull; {description}
                </MenuItem>
              ),
          )}
          <div className={GROUP_NAME_CLASS}>Additional Codes</div>
          {additionalCodes.map(
            ({ code, description }) =>
              code && (
                <MenuItem key={code} value={code} className={menuItemStyle}>
                  {code} &bull; {description}
                </MenuItem>
              ),
          )}
          <MenuItem style={{ display: 'none' }} value={SELECT_PLACEHOLDER_TEXT_VALUE}>
            Select a CPT code
          </MenuItem>
        </Select>
      </div>
    </>
  );
};
